import React from 'react';
import { useAppStateContext } from '../../../AppStateProvider/AppStateProvider';
import { Container, Grid, Typography, Paper, makeStyles } from '@material-ui/core';
import ErrorIllustration from './ErrorIllustration.png';
import { ErrorIcon } from '../../../../icons/ErrorIcon';
import { PrimaryButton } from '../../../common/PrimaryButton';
import { t } from 'i18next';

const useStyles = makeStyles({
  heading: {
    position: 'relative',
    marginTop: '20px',
  },
  paper: {
    display: 'inline-block',
    padding: '23px',
    borderRadius: '8px',
  },
  grantPermissions: {
    fontSize: '16px',
    paddingBottom: '16px',
  },
  /* The size of the image is explicitly stated here so that this content can properly be centered vertically
  before the image is loaded.*/
  illustration: {
    height: '181px',
    width: '342px',
    '@media screen and (max-width: 800px)': {
      marginTop: '12px',
      width: '50vh',
      height: 'auto',
      maxWidth: '100%',
    },
  },
  errorIcon: {
    position: 'absolute',
    right: 'calc(100% + 18px)',
    '@media screen and (max-width: 1000px)': {
      display: 'none',
    },
  },
  gridContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media screen and (max-width: 960px) and (min-width: 761px)': {
      display: 'flex',
      flexWrap: 'nowrap',
    },
    '@media screen and (max-width: 760px)': {
      justifyContent: 'center',
    },
  },
  gridPaper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '@media screen and (max-width: 960px)': {
      justifyContent: 'center',
    },
    '@media screen and (max-width: 760px)': {
      marginTop: '10px',
    },
  },
  gridItemContainer: {
    '@media screen and (max-width: 960px) and (min-width: 761px)': {
      marginRight: '45px',
    },
  },
});

export function getDeviceErrorPaneContent(error?: Error) {
  let headline = '';
  let message = '';

  switch (true) {
    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === 'NotAllowedError':
      headline = t('DEVICE.PERMISSIONS_NEEDED');

      if (error!.message === 'Permission denied by system') {
        // Chrome only
        message = t('DEVICE.OPERATING_SYSTEM_BLOCK');
      } else {
        message = t('DEVICE.NO_PERMISSIONS');
      }

      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === 'NotFoundError':
      headline = t('DEVICE.CANNOT_FIND');
      message = t('DEVICE.NO_CAMERA_MIC');
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headline = t('DEVICE.ERROR');
      message = `${error!.name} ${error!.message}`;
      break;
  }
  return {
    headline,
    message,
  };
}

export function PermissionError() {
  const { state } = useAppStateContext();
  const { headline, message } = getDeviceErrorPaneContent(state.deviceError!);
  const classes = useStyles();

  return (
    <Container>
      <Grid container className={classes.gridContainer}>
        <Grid item md={6} className={classes.gridItemContainer}>
          <Typography variant="h1" gutterBottom className={classes.heading}>
            <div className={classes.errorIcon}>
              <ErrorIcon />
            </div>{' '}
            {headline}
          </Typography>

          <Typography variant="body1" gutterBottom>
            {message}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>{t('DEVICE.UPDATE_YOUR_SETTINGS')}</strong>
          </Typography>

          <PrimaryButton onClick={() => window.location.reload()}>{t('DEVICE.REFRESH_PAGE')}</PrimaryButton>
        </Grid>

        <Grid item md={5} className={classes.gridPaper}>
          <Paper className={classes.paper}>
            <Typography variant="body1" className={classes.grantPermissions}>
              <strong>{t('DEVICE.PERMISSIONS')}</strong>
            </Typography>
            <img src={ErrorIllustration} alt="Settings Illustration" className={classes.illustration} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
